import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClients } from '@/app/ApiProvider';
import type { components } from '@/api/types/api-gateway';

export type Tenant = components['schemas']['Tenant'];
export type TenantCreateRequest = components['schemas']['TenantCreateRequest'];
export type TenantPatchRequest = components['schemas']['TenantPatchRequest'];
export type TenantCreateResponse = components['schemas']['TenantCreateResponse'];

export function useTenants() {
  const clients = useApiClients();
  return useQuery({
    queryKey: ['tenants', 'list'],
    queryFn: async (): Promise<Tenant[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/admin/tenants');
      if (error || !data) throw new Error('tenants list failed');
      return data.items ?? [];
    },
    staleTime: 30_000,
  });
}

export function useTenant(id: string) {
  const clients = useApiClients();
  return useQuery({
    queryKey: ['tenants', id],
    enabled: !!id,
    queryFn: async (): Promise<Tenant> => {
      const { data, error } = await clients.apiGateway.GET(
        '/v1/admin/tenants/{tenantId}',
        { params: { path: { tenantId: id } } },
      );
      if (error || !data) throw new Error('tenant not found');
      return data;
    },
  });
}

export function useCreateTenant() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TenantCreateRequest): Promise<TenantCreateResponse> => {
      const { data, error } = await clients.apiGateway.POST('/v1/admin/tenants', {
        body: vars,
        params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
      });
      if (error || !data) throw new Error('create tenant failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

export function usePatchTenant(id: string) {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TenantPatchRequest) => {
      const { data, error } = await clients.apiGateway.PATCH(
        '/v1/admin/tenants/{tenantId}',
        {
          params: {
            path: { tenantId: id },
            header: { 'Idempotency-Key': crypto.randomUUID() },
          },
          body: vars,
        },
      );
      if (error) throw new Error('patch tenant failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}
